import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactLoading from 'react-loading';
import EnrollmentTemplate from '@/templates/EnrollmentTemplate';
import PATHS from '@/types/navigationPaths';
import contents from '@/assets/config/appInfo';
import { useNavigate } from 'react-router-dom';
import { FetchingStatus } from '@/hooks/types';
import useConfirmGbcAuthImport from '@/hooks/useConfirmGbcAuthImport';
import { Modal, PrimaryButton } from '@/designSystem/components/index';
import { colors } from '@/designSystem/index';
import {
  Content,
  Description,
  LoadingContainer,
  LoadingDescription,
  Margin,
  ModalContainer,
} from './ConfirmationGbcAuthImport.styles';

const ConfirmationGbcAuthImport = () => {
  const navigate = useNavigate();

  const confirmGbcAuthImportState = useConfirmGbcAuthImport();
  const [showErrorModal, setShowErrorModal] = useState(false);

  const { t } = useTranslation('common');

  const goBack = () => {
    navigate(PATHS.SCREEN_ENROLLMENT_PROFILE_SETUP_GBC_AUTH_IMPORT.pathname, { replace: true });
  };

  const onNext = () => {
    if (confirmGbcAuthImportState.fetchingStatus !== FetchingStatus.LOADING) {
      confirmGbcAuthImportState.post(
        { gbcAuthorizationId: '' /** To be implemented */ },
        {
          onSuccess: () => {
            if (window.ReactNativeWebView) {
              const data = JSON.stringify({ to: 'dashboard' });
              window.ReactNativeWebView.postMessage(data);
            }
            navigate(PATHS.SCREEN_DASHBOARD.pathname);
          },
          onError: () => setShowErrorModal(true),
        },
      );
    }
  };

  // Confirmation GBC Auth Import
  document.title = `${t('screens.enrollment.confirmationGbcAuthImportScreen.title')} - ${contents.name}`;
  const isLoading = confirmGbcAuthImportState.fetchingStatus === FetchingStatus.LOADING;
  return (
    <EnrollmentTemplate
      navigationTitle={t('screens.enrollment.confirmationGbcAuthImportScreen.title')}
      navigationPath={PATHS.SCREEN_ENROLLMENT_CONFIRMATION_GBC_AUTH_IMPORT.pathname}
      previousNavigationPath={PATHS.SCREEN_ENROLLMENT_PROFILE_SETUP_GBC_AUTH_IMPORT.pathname}
      onBack={goBack}
      onNext={isLoading ? undefined : onNext} // Hide button while loading
      textNext={t('common.generalButtons.confirm')}
      hideUtilityLogo
      showSupport={false}
      hideSupport
    >
      <Content>
        <Description>{t('screens.enrollment.confirmationGbcAuthImportScreen.description')}</Description>
        <Margin>
          <Description>{t('screens.enrollment.confirmationGbcAuthImportScreen.descriptionBelow')}</Description>
        </Margin>
        {isLoading && (
          <LoadingContainer>
            <ReactLoading type="spin" color={colors.primary} height="25px" width="25px" />
            <LoadingDescription>
              {t('screens.enrollment.confirmationGbcAuthImportScreen.loadingText')}
            </LoadingDescription>
          </LoadingContainer>
        )}
      </Content>

      <ConfirmationErrorModal
        title={t('screens.errorScreen.500.title')}
        description={t('screens.errorScreen.500.description')}
        show={showErrorModal}
        onClose={() => setShowErrorModal(false)}
      />
    </EnrollmentTemplate>
  );
};

const ConfirmationErrorModal = ({
  title,
  show,
  description,
  onClose,
}: {
  title: string;
  show: boolean;
  description: string;
  onClose: () => void;
}) => {
  const { t } = useTranslation('common');

  return (
    <Modal small title={title} show={show} onClose={onClose} showClose>
      <ModalContainer>
        <Description>{description}</Description>
        <PrimaryButton title={t('common.generalButtons.close')} onClick={onClose} />
      </ModalContainer>
    </Modal>
  );
};

export default ConfirmationGbcAuthImport;
